// src/components/dashboard/cases/newCase/case_id/globalStlViewer.jsx
//
// A global, always-mounted STL modal that survives page reloads.
// It owns its own persistence and does NOT depend on CaseTable / caseData.

import React, { useCallback, useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { Modal } from "react-bootstrap";
import STLViewer from "./STLViewer"; // adjust path if STLViewer sits elsewhere
import { lazy, Suspense } from "react";

const LazySTLViewer = lazy(() =>
  import("./STLViewer")
);

// ------------------------------------------------------------------
// localStorage keys
// ------------------------------------------------------------------
const OPEN_KEY = "stlViewer.openFile.v3";

const safeParse = (raw) => {
  if (!raw) return null;
  try { return JSON.parse(raw); } catch { return null; }
};

export const saveOpenStlFile = (file) => {
  try {
    if (!file) {
      localStorage.removeItem(OPEN_KEY);
      return;
    }
    localStorage.setItem(
      OPEN_KEY,
      JSON.stringify({ file, savedAt: Date.now() })
    );
  } catch (e) {
    console.warn("[globalStlViewer] save failed:", e);
  }
};

export const loadOpenStlFile = () => {
  try {
    return safeParse(localStorage.getItem(OPEN_KEY));
  } catch {
    return null;
  }
};

export const clearOpenStlFile = () => {
  try { localStorage.removeItem(OPEN_KEY); } catch { /* ignore */ }
};

// ------------------------------------------------------------------
// Public imperative API — other components call this to open the viewer.
// ------------------------------------------------------------------
let openApiRef = { open: () => {}, close: () => {} };

export const openStlViewer = (file) => openApiRef.open(file);
export const closeStlViewer = () => openApiRef.close();

// ------------------------------------------------------------------
// The global component — mount this ONCE near the root of your app.
// ------------------------------------------------------------------
const API_BASE_URL = "https://hms.backend.hiragroupcloud.com";

const getImageUrl = (path) => {
  if (!path) return "/placeholder.png";
  const filename = path.split(/[\\/]/).pop();
  return `${API_BASE_URL}/uploads/${filename}`;
};

const FileIcon = () => (
  <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <path d="M14 2v6h6" />
    <path d="M8 13h8M8 17h6" />
  </svg>
);

const GlobalStlViewer = () => {
  const [openFile, setOpenFile] = useState(null);
  const [isModelReady, setIsModelReady] = useState(false);
  const [progress, setProgress] = useState(0);

  const closeRef = useRef(null);

  const handleClose = useCallback(() => {
    setOpenFile(null);
    setIsModelReady(false);
    setProgress(0);
    clearOpenStlFile();
  }, []);

  closeRef.current = handleClose;

  // Expose imperative API globally
  useEffect(() => {
    openApiRef.open = (file) => {
      if (!file) return;
      setOpenFile(file);
      setIsModelReady(false);
      setProgress(0);
      saveOpenStlFile(file);
    };
    openApiRef.close = () => closeRef.current?.();

    return () => {
      openApiRef.open = () => {};
      openApiRef.close = () => {};
    };
  }, []);

  // On mount: restore the modal if it was open before reload.
  useEffect(() => {
    const saved = loadOpenStlFile();
    if (!saved || !saved.file) return;

    const p = (saved.file.path || "").split("?")[0].toLowerCase();
    const canView = p.endsWith(".stl") || p.endsWith(".ply");
    if (!canView) {
      clearOpenStlFile();
      return;
    }

    console.log("[globalStlViewer] restoring:", saved.file.name || saved.file._id);
    setOpenFile(saved.file);
    setIsModelReady(false);
    setProgress(0);
  }, []);

  // Block body scroll + close on Escape while open
  useEffect(() => {
    if (!openFile) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => { if (e.key === "Escape") handleClose(); };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onKey);
    };
  }, [openFile, handleClose]);

  if (!openFile) return null;

  const fileName =
    openFile.name ||
    (openFile.path ? openFile.path.split(/[\\/]/).pop() : "") ||
    "File";

  const uploadedBy =
    openFile.uploadedBy ||
    openFile.uploaded_by ||
    openFile.uploaderName ||
    openFile.uploader ||
    openFile.createdBy ||
    openFile.userName ||
    "Unknown User";

  const formatUploadTime = () => {
    const raw = openFile.date || openFile.createdAt || openFile.uploadedAt || openFile.updatedAt;
    if (!raw) return "Unknown";
    try {
      const d = new Date(raw);
      if (isNaN(d.getTime())) return "Unknown";
      return d.toLocaleString("en-GB", {
        day: "2-digit", month: "short", year: "numeric",
        hour: "2-digit", minute: "2-digit", hour12: true,
      });
    } catch { return "Unknown"; }
  };

  const uploadTime = formatUploadTime();
  const fileUrl = getImageUrl(openFile.path);

  return ReactDOM.createPortal(
    <div
      style={{
        position: "fixed", inset: 0, width: "100vw", height: "100vh",
        display: "flex", justifyContent: "center", alignItems: "center",
        backgroundColor: "rgba(0, 0, 0, 0.85)", zIndex: 99999, padding: "12px",
        boxSizing: "border-box",
      }}
      onClick={handleClose}
    >
      <div
        style={{
          width: "min(1400px, 96vw)", height: "min(850px, 88vh)",
          backgroundColor: "#00a1d6", borderRadius: "10px",
          display: "flex", flexDirection: "column",
          boxShadow: "0 20px 60px rgba(0,0,0,0.5)", overflow: "hidden",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* HEADER */}
        <div
          style={{
            backgroundColor: "#004aad",
            backgroundImage: "linear-gradient(115deg, #004aad 0%, #003b88 100%)",
            color: "#fff", padding: "14px 22px",
            display: "flex", alignItems: "center", justifyContent: "space-between",
            gap: "20px", flexShrink: 0, minHeight: "76px", boxSizing: "border-box",
            borderBottom: "1px solid rgba(255,255,255,0.18)",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "12px", minWidth: 0, flex: "1 1 auto" }}>
            <div style={{
              width: "42px", height: "42px", minWidth: "42px",
              display: "flex", alignItems: "center", justifyContent: "center",
              backgroundColor: "rgba(255,255,255,0.13)",
              border: "1px solid rgba(255,255,255,0.2)", borderRadius: "10px",
            }}>
              <FileIcon />
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: "4px", minWidth: 0 }}>
              <div style={{
                fontSize: "17px", fontWeight: 700, lineHeight: 1.35,
                whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
              }} title={fileName}>{fileName}</div>
              <div style={{
                fontSize: "11px", color: "rgba(255,255,255,0.72)",
                letterSpacing: "0.7px", textTransform: "uppercase", fontWeight: 500,
              }}>3D Model Viewer</div>
            </div>
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: "20px", flexShrink: 0 }}>
            <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
              <span style={{ fontSize: "10px", color: "rgba(255,255,255,0.68)",
                textTransform: "uppercase", letterSpacing: "0.6px" }}>Uploaded By</span>
              <span style={{ fontSize: "13px", fontWeight: 600 }}>{uploadedBy}</span>
            </div>
            <div style={{ width: "1px", height: "34px", backgroundColor: "rgba(255,255,255,0.25)" }} />
            <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
              <span style={{ fontSize: "10px", color: "rgba(255,255,255,0.68)",
                textTransform: "uppercase", letterSpacing: "0.6px" }}>Uploaded At</span>
              <span style={{ fontSize: "12px", fontWeight: 600 }}>{uploadTime}</span>
            </div>
            <button
              onClick={handleClose}
              aria-label="Close STL viewer"
              style={{
                backgroundColor: "rgba(255,255,255,0.12)", color: "#fff",
                border: "1px solid rgba(255,255,255,0.2)", borderRadius: "8px",
                width: "36px", height: "36px", display: "inline-flex",
                alignItems: "center", justifyContent: "center",
                fontSize: "18px", cursor: "pointer", flexShrink: 0,
              }}
            >&#10005;</button>
          </div>
        </div>

        {/* BODY */}
        <div style={{ position: "relative", flex: 1, minHeight: 0, width: "100%" }}>
          {!isModelReady && (
            <div style={{
              position: "absolute", inset: 0, display: "flex", flexDirection: "column",
              alignItems: "center", justifyContent: "center",
              backgroundColor: "#00a1d6", zIndex: 5,
            }}>
              <div style={{
                width: "50px", height: "50px",
                border: "4px solid rgba(255,255,255,0.3)",
                borderTopColor: "#fff", borderRadius: "50%",
                animation: "stl-modal-spin 0.8s linear infinite",
              }} />
              <p style={{ color: "#fff", marginTop: "15px", fontSize: "16px" }}>
                Loading 3D Model...{progress > 0 && progress < 100 ? ` ${progress}%` : ""}
              </p>
              <style>{`@keyframes stl-modal-spin { to { transform: rotate(360deg); } }`}</style>
            </div>
          )}

          <div style={{ width: "100%", height: "100%" }}>
            <Suspense fallback={null}>
              <LazySTLViewer
                fileUrl={fileUrl}
                sessionKey={openFile._id || openFile.id || fileUrl}
                size="extra-large"
                shouldLoad={true}
                onProgress={setProgress}
                onLoad={() => setIsModelReady(true)}
                onError={() => setIsModelReady(true)}
              />
            </Suspense>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default GlobalStlViewer;